import React, { useState, useEffect } from 'react';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '0.00';
  return Number(value)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr)?.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getTodayDate = () => {
  const today = new Date();
  return today?.toISOString()?.split('T')?.[0];
};

const TransactionJournalPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [journals, setJournals] = useState([]);
  const [expandedJournals, setExpandedJournals] = useState({});

  const today = getTodayDate();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [transactionNumber, setTransactionNumber] = useState('');

  const fetchJournals = async () => {
    if (!startDate || !endDate) return;
    try {
      setLoading(true);
      setError(null);
      const params = { start_date: startDate, end_date: endDate };
      if (transactionNumber?.trim()) {
        params.transaction_number = transactionNumber?.trim();
      }
      const response = await apiService?.get('/portal/financials/journals', params);
      const data = response?.data;
      if (data?.status_code === 200) {
        const parsed = (data?.details || [])?.map((entry) => {
          let lines = [];
          try {
            lines = typeof entry?.lines === 'string' ? JSON.parse(entry?.lines) : (entry?.lines || []);
          } catch {
            lines = [];
          }
          return { ...entry, parsedLines: lines };
        });
        setJournals(parsed);
        // Auto-expand all entries
        const expanded = {};
        parsed?.forEach((j) => { expanded[j?.journal_id] = true; });
        setExpandedJournals(expanded);
      } else {
        setError(data?.status_description || 'Failed to load journal entries');
      }
    } catch (err) {
      setError(err?.response?.data?.status_description || 'Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJournals();
  }, []);

  const toggleJournal = (journalId) => {
    setExpandedJournals(prev => ({ ...prev, [journalId]: !prev?.[journalId] }));
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchJournals();
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main
          className={`flex-1 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          } mt-16 p-6`}
        >
          {/* Breadcrumb & Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-1">
              <span>Finance</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Transaction Journal</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Transaction Journal</h1>
                <p className="text-text-secondary text-sm mt-1">View journal entries and their accounting lines</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className="bg-surface border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex flex-col gap-1 min-w-[160px]">
                <label className="text-xs font-medium text-text-secondary">Start Date <span className="text-error">*</span></label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e?.target?.value)}
                  required
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex flex-col gap-1 min-w-[160px]">
                <label className="text-xs font-medium text-text-secondary">End Date <span className="text-error">*</span></label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e?.target?.value)}
                  required
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="flex flex-col gap-1 min-w-[200px]">
                <label className="text-xs font-medium text-text-secondary">Transaction Number <span className="text-text-secondary text-xs font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={transactionNumber}
                  onChange={(e) => setTransactionNumber(e?.target?.value)}
                  placeholder="e.g. D-a9ee4ad-10"
                  className="border border-border rounded-lg px-3 py-2 text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <button
                type="submit"
                disabled={!startDate || !endDate || loading}
                className="flex items-center space-x-2 bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="Search" size={15} />
                <span>Search</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(today);
                  setEndDate(today);
                  setTransactionNumber('');
                }}
                className="flex items-center space-x-2 border border-border text-text-secondary px-4 py-2 rounded-lg text-sm hover:bg-surface-secondary transition-colors"
              >
                <Icon name="RotateCcw" size={14} />
                <span>Reset</span>
              </button>
            </div>
          </form>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-text-secondary">Loading journal entries...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Icon name="AlertCircle" size={40} className="text-error mb-3" />
              <p className="text-text-secondary">{error}</p>
              <button onClick={fetchJournals} className="mt-4 text-primary text-sm hover:underline">Retry</button>
            </div>
          ) : journals?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Icon name="BookOpen" size={40} className="text-text-secondary mb-3" />
              <p className="text-text-primary font-medium">No journal entries found</p>
              <p className="text-text-secondary text-sm mt-1">Try adjusting your date range or transaction number filter</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-text-secondary">{journals?.length} journal entr{journals?.length !== 1 ? 'ies' : 'y'} found</p>
              {journals?.map((journal) => {
                const isExpanded = expandedJournals?.[journal?.journal_id];
                const totalDebit = journal?.parsedLines?.reduce((sum, l) => sum + Number(l?.debit || 0), 0);
                const totalCredit = journal?.parsedLines?.reduce((sum, l) => sum + Number(l?.credit || 0), 0);

                return (
                  <div key={journal?.journal_id} className="bg-surface border border-border rounded-lg overflow-hidden">
                    {/* Journal Header */}
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-surface-secondary/50 transition-colors text-left"
                      onClick={() => toggleJournal(journal?.journal_id)}
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="BookOpen" size={16} className="text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                            <span className="text-xs font-mono bg-surface-secondary border border-border rounded px-2 py-0.5 text-text-secondary">
                              #{journal?.journal_id}
                            </span>
                            <span className="text-sm font-semibold text-primary">{journal?.transaction_number}</span>
                            <span className="text-xs text-text-secondary">{formatDate(journal?.journal_date)}</span>
                          </div>
                          <p className="text-sm text-text-secondary mt-1">{journal?.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 flex-shrink-0 ml-4">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-text-secondary">{journal?.parsedLines?.length} line{journal?.parsedLines?.length !== 1 ? 's' : ''}</p>
                        </div>
                        <Icon
                          name="ChevronDown"
                          size={16}
                          className={`text-text-secondary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </button>

                    {/* Lines Sub-table */}
                    {isExpanded && (
                      <div className="border-t border-border">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-surface-secondary/50 border-b border-border">
                                <th className="text-left px-5 py-2.5 text-text-secondary font-medium text-xs">Account Code</th>
                                <th className="text-left px-5 py-2.5 text-text-secondary font-medium text-xs">Account Name</th>
                                <th className="text-right px-5 py-2.5 text-text-secondary font-medium text-xs">Debit</th>
                                <th className="text-right px-5 py-2.5 text-text-secondary font-medium text-xs">Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {journal?.parsedLines?.map((line, idx) => (
                                <tr key={idx} className="border-b border-border last:border-0 hover:bg-surface-secondary/30 transition-colors">
                                  <td className="px-5 py-2.5 font-mono text-text-primary font-medium text-xs">{line?.account_code}</td>
                                  <td className="px-5 py-2.5 text-text-primary">{line?.account_name}</td>
                                  <td className="px-5 py-2.5 text-right">
                                    {Number(line?.debit) > 0 ? (
                                      <span className="text-success font-medium">{formatNumber(line?.debit)}</span>
                                    ) : (
                                      <span className="text-text-secondary">-</span>
                                    )}
                                  </td>
                                  <td className="px-5 py-2.5 text-right">
                                    {Number(line?.credit) > 0 ? (
                                      <span className="text-error font-medium">{formatNumber(line?.credit)}</span>
                                    ) : (
                                      <span className="text-text-secondary">-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-surface-secondary/60 border-t border-border">
                                <td colSpan={2} className="px-5 py-2.5 text-xs font-semibold text-text-primary">Totals</td>
                                <td className="px-5 py-2.5 text-right text-xs font-semibold text-success">{formatNumber(totalDebit)}</td>
                                <td className="px-5 py-2.5 text-right text-xs font-semibold text-error">{formatNumber(totalCredit)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TransactionJournalPage;
